import { FormikProvider } from 'formik';
import Link from 'next/link';
import { useRouter } from 'next/router';
import {
  type ForwardedRef,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  getBookingTotal,
  getConflicts,
  getMaxDate,
  getSlotsForSpace,
  isClinical,
  slotHasError,
} from 'utils/booking';
// utils
import { generateTimeList as timeOptions } from 'utils/date';
import moment from 'utils/dayjs';
import { useTranslation } from 'utils/i18n';
// hooks
import useAvailability from '../../hooks/useAvailability';
import useBookingHosts from '../../hooks/useBookingHosts';
import useBookingItems from '../../hooks/useBookingItems';
import useBookingSlots from '../../hooks/useBookingSlots';
import useClientServices from '../../hooks/useClientServices';
import useFormikBooking from '../../hooks/useFormikBooking';
import useIsMobile from '../../hooks/useIsMobile';
import useUpdateEffect from '../../hooks/useUpdateEffect';
import useVenue, { Venue } from '../../hooks/useVenue';
import useVenueOpeningTimes from '../../hooks/useVenueOpeningTimes';
import { AuthContext } from '../../state/auth';
import { SearchContext } from '../../state/search';
// components
import AddToBasketButton from '../addToBasketButton';
import AddToBasketModal from '../addToBasketModal';
import BookingHostForm from '../bookingHostForm';
import BookingTotal from '../bookingTotal';
import DatepickerField from '../fields/datepickerField';
import Field from '../fields/field';
import SelectList from '../fields/selectList';
import ServiceSelect from '../fields/serviceSelect';
import SlotsField from '../fields/slotsField';
import HostSelect from '../hostSelect';
import ModalDialog from '../modal';
import RecurrenceOptions from '../recurrenceOptions';
import SpaceTimeSlots from '../spaceTimeSlots';

interface Props {
  initialValues?: object;
}

export const slotNonEmpty = ({
  startTime,
  endTime,
}: {
  startTime?: unknown;
  endTime?: unknown;
}) => !!startTime && !!endTime;

const QuickBook = (
  { initialValues }: Props,
  forwardedRef: ForwardedRef<HTMLDivElement>
) => {
  const { t } = useTranslation('common');
  const quickBookRef = useRef<HTMLDivElement | null>(null);

  const router = useRouter();

  const { loggedIn, accountApproved } = useContext(AuthContext);
  const {
    filters: { date: searchDate },
  } = useContext(SearchContext);
  const [showBookingHostForm, toggleBookingHostForm] = useState(false);
  const [addToBasketOpen, setAddToBasketOpen] = useState(false);
  const isMobile = useIsMobile();

  const {
    data: services = [],
    bookableServices = [],
    loading: servicesLoading,
  } = useClientServices();

  const onSubmit = async () => setAddToBasketOpen(true);

  const {
    formik,
    initialising,
    generateBookingItems,
    clearAll: defaultClearAll,
    combinedSlots,
  } = useFormikBooking(
    true,
    { initialValues, enableReinitialize: addToBasketOpen, onSubmit },
    true,
    {
      services,
    }
  );

  const { values, setFieldValue, handleSubmit, isSubmitting } = formik;

  const { spaceIds, venueId } = values;

  const updatesEnabled = !initialising;

  const { data: venue = {} as Venue, loading: venueLoading } = useVenue(
    venueId,
    true
  );
  const { spaces: venueSpaces = [] } = venue;

  const spaces = useMemo(
    () => venueSpaces.filter(({ id }) => spaceIds.includes(id)),
    [venueSpaces, spaceIds]
  );

  const slots = useMemo(
    () => values.slots.filter(slotNonEmpty),
    [values.slots]
  );

  const onCheckRecurrence = (clashesDetected?: boolean) => {
    if (clashesDetected) {
      if (!isMobile) router.push(t('navigation.advanced_booking.url'));
      return;
    }

    setFieldValue('bookingItems', generateBookingItems());
  };

  const clearAll = () =>
    defaultClearAll({ startTime: searchDate || undefined });

  useEffect(() => {
    if (searchDate) {
      setFieldValue('date', moment(searchDate).startOf('day').toDate());
    }
  }, [setFieldValue, searchDate]);

  useEffect(() => {
    if (values.date === undefined) {
      setFieldValue('date', moment().startOf('day').toDate());
    }
  }, [setFieldValue, values.date]);

  const deselectSpace = ({ id: spaceId }: { id: number }) =>
    spaceIds.length === 1
      ? clearAll()
      : setFieldValue(
          'spaceIds',
          spaceIds.filter((id: number) => id !== spaceId)
        );

  const [{ startTime: firstStartTime, endTime: firstEndTime } = {}] =
    values.slots;

  useEffect(() => {
    if (!firstStartTime) return;

    const minEndTime = moment(firstStartTime, 'HH:mm').add(1, 'hour');

    if (!firstEndTime || moment(firstEndTime, 'HH:mm').isBefore(minEndTime))
      setFieldValue('slots.0.endTime', minEndTime.format('HH:mm'));
  }, [setFieldValue, firstStartTime, firstEndTime]);

  const clinical = useMemo(() => isClinical(spaces), [spaces]);

  useUpdateEffect(
    () => {
      setFieldValue('slots', []);
    },
    [setFieldValue],
    updatesEnabled && !venueLoading
  );

  const maxDate = useMemo(
    () =>
      getMaxDate(
        moment().toDate(),
        services.find(({ id }) => Number(id) === Number(values.service))
      ),
    [services, values.service]
  );

  const {
    data: { openingTime, closingTime },
    loading: openingTimesLoading,
  } = useVenueOpeningTimes(values.date, venueId);

  const lastBookingTimeForVenue = useMemo(
    () => moment(closingTime, 'HH:mm').subtract(1, 'hour').format('HH:mm'),
    [closingTime]
  );

  const startTime = openingTime;
  const endTime = closingTime;

  const { data: availability, loading: availabilityLoading } = useAvailability(
    values.date,
    values.service,
    spaces as unknown as Parameters<typeof useAvailability>[2],
    combinedSlots,
    values.recurrence,
    undefined,
    values.id ? Number(values.id) : undefined
  );

  const bookingTotal = useMemo(
    () =>
      (getBookingTotal as (availability: unknown, spaces?: unknown) => number)(
        availability,
        spaces
      ),
    [availability, spaces]
  );

  const clashCount = useMemo(
    () => getConflicts(availability).length,
    [availability]
  );

  const hasBookingConflicts =
    values.recurrence.enabled === values.recurrence.checked && clashCount > 0;

  const addToBasketButtonIsDisabled =
    !slots.length ||
    !values.host ||
    !values.host.value ||
    !values.service ||
    hasBookingConflicts ||
    (values.recurrence.enabled ? !values.recurrence.checked : false) ||
    isSubmitting;

  const { data: bookingSlots } = useBookingSlots(values.date, venueId);
  const { data: bookingItems } = useBookingItems(values.date, spaceIds);

  const { data: bookingHosts, add: addBookingHost } = useBookingHosts();

  const hostFormSubmit = async (host: object) => {
    const { id: value, label } = await addBookingHost(host);
    setFieldValue('host', { label, value });
    toggleBookingHostForm(false);
  };

  const renderAdvancedBookingLink = () => {
    // if mobile and conflicts exist, we show a warning that advanced booking not available
    if (isMobile && hasBookingConflicts) {
      return (
        <div className="quick-book__clash-alert quick-book__clash-alert--faq">
          <p className="quick-book__clash-alert-text">
            {t('quick_book.timeslot_widget.conflict_faq_warning')}{' '}
            <Link
              href={t('navigation.faq.url')}
              className="quick-book__clash-alert-link"
            >
              {t('quick_book.timeslot_widget.conflict_faq_warning_link')}
            </Link>
          </p>
        </div>
      );
    }

    // if mobile or conflicts exist, hide advanced booking link
    if (isMobile || hasBookingConflicts) {
      return null;
    }

    // otherwise show the advanced booking link
    return (
      <div className="quick-book__link">
        <Link
          href={t('navigation.advanced_booking.url')}
          className="button button--text"
        >
          {t('quick_book.go_to_advanced_booking')}
        </Link>
      </div>
    );
  };

  const setRefs = useCallback(
    (ref: HTMLDivElement | null) => {
      if (typeof forwardedRef === 'function') forwardedRef(ref);
      // eslint-disable-next-line no-param-reassign
      else if (forwardedRef) forwardedRef.current = ref;
      quickBookRef.current = ref;
    },
    [forwardedRef]
  );

  return (
    <FormikProvider value={formik}>
      <div
        className="block block--with-border"
        id="quickBook"
        data-cy="bookingWidget"
        ref={setRefs}
      >
        <div className="block__inner">
          <div className="block__title">
            <h3 className="block__title-heading" data-testid="title">
              {t('quick_book.default_title')}
            </h3>

            {!!spaces.length && (
              <div className="block__title-action">
                <button
                  className="button button--text"
                  data-testid="clearAll"
                  onClick={clearAll}
                >
                  {t('shared.clear_all')}
                </button>
              </div>
            )}
          </div>

          {loggedIn && (
            <>
              {!accountApproved && (
                <p data-testid="description">
                  {t('quick_book.account_unapproved_message')}
                </p>
              )}

              {accountApproved && !!spaces.length && (
                <>
                  <div>
                    <Field
                      name="service"
                      as={ServiceSelect}
                      setValueProp="onChange"
                      services={bookableServices}
                      clinical={clinical}
                      loading={servicesLoading}
                      autoSelect={updatesEnabled}
                    />

                    <div data-cy="datepicker">
                      <Field
                        name="date"
                        as={DatepickerField}
                        setValueProp="onChange"
                        maxDate={maxDate}
                        dataCy="bookingWidgetDatePicker"
                      />
                    </div>

                    {!clinical && (
                      <div className="field-multicol">
                        <Field
                          name="slots.0.startTime"
                          as={SelectList}
                          setValueProp="onChange"
                          label={t('shared.start_time')}
                          placeholder={t('shared.start_time')}
                          options={timeOptions(
                            startTime,
                            lastBookingTimeForVenue
                          )}
                          disabled={!values.service && !values.date}
                          loading={openingTimesLoading}
                          testid="selectStartTime"
                        />

                        <Field
                          name="slots.0.endTime"
                          as={SelectList}
                          setValueProp="onChange"
                          label={t('shared.end_time')}
                          placeholder={t('shared.end_time')}
                          options={timeOptions(
                            firstStartTime
                              ? moment(firstStartTime, 'HH:mm').format('HH:mm')
                              : startTime,
                            endTime,
                            true
                          )}
                          disabled={!firstStartTime}
                          loading={openingTimesLoading}
                          testid="selectEndTime"
                        />
                      </div>
                    )}
                  </div>

                  <Field
                    name="slots"
                    as={SlotsField}
                    setValueProp="onChange"
                    clinical={clinical}
                    date={values.date}
                    bookingItems={bookingItems}
                    slots={bookingSlots}
                    availability={availability}
                    venue={venue}
                    spaces={spaces}
                    disabled={!values.date || !values.service}
                    loading={availabilityLoading}
                    openingTime={openingTime}
                    startTime={startTime}
                    endTime={endTime}
                  />
                  {venue.id && spaces && spaces.length && (
                    <h4 className="quick-book__selected_venue">
                      <Link href={`${t('navigation.venues.url')}/${venue.id}`}>
                        {venue.name}
                      </Link>
                    </h4>
                  )}

                  <ul
                    className="space-list"
                    data-cy="rooms"
                    id="selected_spaces"
                  >
                    {spaces.map(space => {
                      const spaceSlots = slots.length
                        ? getSlotsForSpace(
                            space.id,
                            slots,
                            availability,
                            availabilityLoading
                          )
                        : [];

                      return (
                        <SpaceTimeSlots
                          key={space.id}
                          space={space}
                          slots={spaceSlots}
                          onRemove={() => deselectSpace(space)}
                          hasError={slotHasError(spaceSlots)}
                          hasRecurrence={values.recurrence.enabled}
                          availability={availability}
                        />
                      );
                    })}
                  </ul>

                  <Link
                    href={t('navigation.search.url')}
                    className="quick-book__add_room"
                  >
                    {t('quick_book.add_another_room')}
                  </Link>

                  <RecurrenceOptions
                    disabled={
                      hasBookingConflicts && !values.recurrence.enabled
                        ? true
                        : undefined
                    }
                    updatesEnabled={updatesEnabled}
                    loading={availabilityLoading}
                    clashCount={clashCount}
                    className="recurrence__quickbook"
                    onCheck={onCheckRecurrence}
                    maxDate={maxDate}
                  />

                  {renderAdvancedBookingLink()}

                  <BookingTotal total={bookingTotal} />

                  <div className="host-select">
                    <Field
                      name="host"
                      as={HostSelect}
                      setValueProp="onChange"
                      bookingHosts={bookingHosts}
                    />

                    <div className="quick-book__link">
                      <button
                        type="button"
                        className="button button--text"
                        onClick={() => toggleBookingHostForm(true)}
                      >
                        {t('quick_book.add_new_host')}
                      </button>
                    </div>
                  </div>

                  <div className="block__submit">
                    <AddToBasketButton
                      type="submit"
                      onClick={handleSubmit}
                      conflict={hasBookingConflicts && !isMobile}
                      disabled={addToBasketButtonIsDisabled}
                      loading={isSubmitting}
                      data-testid="addToBasketButton"
                    />
                  </div>
                </>
              )}

              {accountApproved && !spaces.length && (
                <p className="no-margin" data-testid="description">
                  {t('quick_book.add_rooms_message')}
                </p>
              )}
            </>
          )}
          {!loggedIn && (
            <>
              <p className="intro" data-testid="description">
                {t('quick_book.register_message')}
              </p>
              <Link
                href={t('navigation.register.url')}
                data-testid="registerLink"
                className="button button--block button--primary"
              >
                {t('shared.register_now')}
              </Link>
              <p className="note">
                {t('quick_book.login_message')}{' '}
                <Link href={t('navigation.sign_in.url')}>
                  {t('navigation.sign_in.text')}
                </Link>
              </p>
            </>
          )}
        </div>
      </div>

      <AddToBasketModal
        isOpen={addToBasketOpen}
        onClose={() => setAddToBasketOpen(false)}
      />

      <ModalDialog
        isOpen={showBookingHostForm}
        onClose={() => {
          toggleBookingHostForm(false);
        }}
        headerLabel={t('booking_hosts.modal.title')}
        primaryAction={
          <button
            type="submit"
            form="bookingHostForm"
            className="button button--secondary"
            data-testid="bookingHostFormButton"
          >
            {t('booking_hosts.form.button')}
          </button>
        }
      >
        <BookingHostForm
          onSubmit={hostFormSubmit}
          bookingHosts={bookingHosts}
        />
      </ModalDialog>
    </FormikProvider>
  );
};

export default forwardRef(QuickBook);
